/**
 * Where a staff invitation lands (MSG-032). The invited person signs in with that email, then
 * accepts. A mail scanner fetching the link does nothing.
 */
import { useEffect, useState } from "react";
import * as stylex from "@stylexjs/stylex";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import { Banner } from "@lattice/ui/Banner";
import { Button } from "@lattice/ui/Button";
import { EmptyState } from "@lattice/ui/EmptyState";
import { ErrorState } from "@lattice/ui/ErrorState";
import { LinkButton } from "@lattice/ui/LinkButton";
import { Pending } from "@lattice/ui/Pending";
import { Text } from "@lattice/ui/Text";
import type { ClinicInvitationInfo } from "@lattice/core";
import { acceptClinicInvite, loadClinicInvitation, signOut } from "../api.ts";
import { navigate } from "../router.ts";
import { ClinicSignIn } from "./SignIn.tsx";
import { ClinicShell } from "./Shell.tsx";
import { loadClinic } from "../api.ts";
/** "an owner and practitioner". */
function rolesText(offer: ClinicInvitationInfo): string {
  const names = offer.roles.map(role => role);
  const last = names.pop();
  if (last === undefined) return "a member";
  const list = names.length === 0 ? last : `${names.join(", ")} and ${last}`;
  return `${/^[aeiou]/.test(list) ? "an" : "a"} ${list}`;
}
export function ClinicAccept() {
  const invitationId = new URLSearchParams(window.location.search).get("invitation") ?? "";
  const callbackURL = `/clinic/accept?invitation=${encodeURIComponent(invitationId)}`;
  const [phase, setPhase] = useState<"loading" | "sign-in" | "ready" | "not-open" | "failed">("loading");
  const [offer, setOffer] = useState<ClinicInvitationInfo | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    void loadClinic().then(loaded => {
      if (loaded.status === "signed_out") setPhase("sign-in");else if (loaded.status === "ok") void showOffer();else setPhase("failed");
    }, () => setPhase("failed"));
  }, []);
  async function showOffer() {
    const read = await loadClinicInvitation(invitationId).catch(() => null);
    if (read === null || read.status === "failed" || read.status === "signed_out") {
      setPhase("failed");
      return;
    }
    if (read.status === "not_open") {
      setPhase("not-open");
      return;
    }
    setOffer(read.invitation);
    setPhase("ready");
  }
  async function accept() {
    setBusy(true);
    setError(null);
    const result = await acceptClinicInvite(invitationId).catch(() => null);
    setBusy(false);
    if (result === null) {
      setError("This didn't go through. Try again in a minute.");
      return;
    }
    if (result.status !== "ok") {
      setError("This invitation could not be accepted. Sign in with the invited email.");
      return;
    }
    navigate("/clinic");
  }
  if (invitationId === "") return <ClinicShell title="Join a clinic">
        <EmptyState variant="page" title="This invitation is incomplete" body="Ask the clinic to send a new one." />
      </ClinicShell>;
  if (phase === "loading") return <ClinicShell title="Join a clinic">
        <Pending message="Opening the invitation" bones={["title", "line"]} />
      </ClinicShell>;
  if (phase === "sign-in") return <ClinicSignIn callbackURL={callbackURL} />;
  if (phase === "not-open") return <ClinicShell title="This invitation is not open" description="It may have been withdrawn, used already or sent to a different email address. Sign in with the invited email, or ask the clinic to send a new one." signedIn width="narrow">
        <div className="x78zum5 x1a02dak x626il7">
          <Button testId="not-open-sign-out" corners="rounded" onClick={() => void signOut().then(() => window.location.reload())}>
            Sign out and use another email
          </Button>
          <LinkButton href="/clinic" corners="rounded" navigate={navigate}>
            Go to the clinic home
          </LinkButton>
        </div>
      </ClinicShell>;
  if (phase === "failed") return <ClinicShell title="Join a clinic">
        <ErrorState variant="page" title="We couldn't open this invitation" body="Try again in a minute. If it keeps happening, ask the clinic to send a new one." onRetry={() => window.location.reload()} />
      </ClinicShell>;
  return <ClinicShell title={`Join ${offer?.clinicName ?? "this clinic"}`} signedIn width="narrow">
      <Text tone="secondary" testId="invite-offer">
        You are invited as {offer === null ? "a member" : rolesText(offer)}.
      </Text>
      {error === null ? null : <Banner tone="danger" title="Not joined">
          {error}
        </Banner>}
      <Button testId="accept-invite" corners="rounded" loading={busy} fullWidth onClick={() => void accept()}>
        {busy ? "Joining…" : "Accept invitation"}
      </Button>
    </ClinicShell>;
}